const pick = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

export const toAmount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (Array.isArray(value?.data)) return value.data.filter(Boolean);
  return [];
};

export const toLots = (volume) => {
  const v = parseFloat(volume);
  if (!v || Number.isNaN(v)) return 0;
  return v >= 100 ? v / 10000 : v;
};

const DONUT_COLORS = ['#8b0000', '#6366F1', '#F97316', '#10B981', '#EC4899', '#64748B'];
const DONUT_TOP_N = 5;

const formatPct = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

export const parseEarningDashboardTrend = (data) => {
  const list = toList(pick(data, 'commissionTrend', 'commissionTrends'));
  if (!list.length) return null;

  const labels = [];
  const amounts = [];
  const percentages = [];

  list.forEach((item) => {
    labels.push(item.month || item.label || '');
    amounts.push(toAmount(item.amount));
    percentages.push(item.percentage != null ? formatPct(item.percentage) : null);
  });

  return labels.some(Boolean) ? { labels, amounts, percentages } : null;
};

export const parseEarningDashboardBreakdown = (data) => {
  const list = toList(pick(data, 'commissionBreakDown', 'commissionBreakdown', 'breakdown'));
  const activeItems = list.filter((item) => toAmount(item.amount) > 0);

  if (!activeItems.length) {
    return { segments: [], list: [], total: 0, donutSegments: [] };
  }

  const items = activeItems
    .map((item, index) => ({
      label: item.symbol || item.label || '—',
      value: toAmount(item.amount),
      pct: item.percentage != null ? formatPct(item.percentage) : null,
      color: DONUT_COLORS[index % DONUT_COLORS.length],
    }))
    .sort((a, b) => b.value - a.value);

  const top = items.slice(0, DONUT_TOP_N);
  const othersValue = items.slice(DONUT_TOP_N).reduce((sum, item) => sum + item.value, 0);
  const othersPct = items
    .slice(DONUT_TOP_N)
    .reduce((sum, item) => sum + (item.pct != null ? Number(item.pct) : 0), 0);

  const donutSegments = othersValue > 0
    ? [
        ...top,
        {
          label: 'Others',
          value: othersValue,
          pct: othersPct > 0 ? othersPct.toFixed(2) : null,
          color: '#94A3B8',
        },
      ]
    : top;

  return {
    segments: items,
    list: items,
    donutSegments,
    total: items.reduce((sum, item) => sum + item.value, 0),
  };
};

export const parseEarningDashboardSummary = (data) => {
  const summary = pick(data, 'earningSummary');
  if (!summary || Array.isArray(summary)) {
    return {
      hasData: false,
      totalClients: 0,
      totalLots: 0,
      totalProfit: 0,
      totalRecords: 0,
    };
  }

  return {
    hasData: true,
    totalClients: toAmount(summary.totalClients),
    totalLots: toAmount(summary.totalLots),
    totalProfit: toAmount(summary.totalProfit),
    totalRecords: toAmount(summary.totalRecords),
  };
};

export const parseEarningDashboard = (response) => {
  if (!response?.success) return null;

  const data = response?.data ?? response?.result ?? null;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const breakdown = parseEarningDashboardBreakdown(data);
  const earningSummary = parseEarningDashboardSummary(data);
  const trend = parseEarningDashboardTrend(data);

  const totalEarned = toAmount(pick(data, 'totalCommissionEarned'));
  const pending = toAmount(pick(data, 'pendingCommission'));
  const paid = toAmount(pick(data, 'paidCommission'));
  const avgPerLot = toAmount(pick(data, 'averageCommissionLot', 'averageCommissionPerLot'));

  const hasData =
    totalEarned > 0 ||
    pending > 0 ||
    paid > 0 ||
    avgPerLot > 0 ||
    Boolean(trend) ||
    breakdown.segments.length > 0 ||
    earningSummary.hasData;

  if (!hasData) return null;

  return {
    hasData: true,
    totalEarned,
    pending,
    paid,
    avgPerLot,
    trend,
    breakdown,
    earningSummary,
  };
};
